// infrastructure/http/repositories/PascoRepositoryHttp.ts
import type { Pasco } from "@src/domain/pascos/entities/pasco.entity";
import type { IPascoRepository, SearchPascoParams, SearchPascoResult } from "@src/domain/pascos/repository/pasco_repository";
import type { ApiClient } from "../api/client";
import { PascoMap } from "../mappers/pasco_mapper";
import type { CreatePascoData } from "@src/domain/pascos/value_objects/create_pasco_data";
import type { StudentUploadPascoData } from "@src/domain/pascos/value_objects/student_upload_pasco_data";
import { PresignedUrl } from "@src/domain/pascos/value_objects/presigned_url";
import { PreviewUrl } from "@src/domain/pascos/value_objects/preview_url";
import type { ExtractedMetadataDTO } from "@src/application/pascos/dto";


export class PascoRepositoryImpl implements IPascoRepository {
    private readonly apiClient: ApiClient;

    constructor(apiClient: ApiClient) {
        this.apiClient = apiClient;
    }

    async runPipeline(): Promise<void> {
        await this.apiClient.get("/run-pipeline");
    }

    async listAll(): Promise<Pasco[]> {
        const data = await this.apiClient.get<any[]>("/pascos");

        // Ensure data is an array
        if (!Array.isArray(data)) {
            console.warn('API returned unexpected data structure for pascos list:', data);
            return [];
        }

        return data.map((p: any) => PascoMap.toDomain(p));
    }

    async getById(pascoId: string): Promise<Pasco | null> {
        const data = await this.apiClient.get<any>(`/pascos/${pascoId}`);
        return data ? PascoMap.toDomain(data) : null;
    }

    async create(data: CreatePascoData, onUploadProgress?: (progress: number) => void): Promise<Pasco> {
        // Upload the PDF file along with the form data
        const response = await this.apiClient.uploadFile<any>("/pascos", {
            file: data.file,
            additionalData: {
                course_code: data.course_code,
                course_name: data.course_name,
                semester: data.semester,
                academic_year: data.academic_year,
                course_description: data.course_description,
                department: data.department,
                is_interim_assessment: data.is_interim_assessment,
                is_supplementary: data.is_supplementary,
                level: data.level,
                university_id: data.university_id,
            },
            onUploadProgress: onUploadProgress ? (progressEvent: any) => {
                if (progressEvent.total) {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    onUploadProgress(percentCompleted);
                }
            } : undefined,
        });

        return PascoMap.toDomain(response);
    }

    async studentUpload(data: StudentUploadPascoData, onUploadProgress?: (progress: number) => void): Promise<Pasco> {
        // Upload the PDF file with optional university_id for student uploads
        const additionalData: any = {};
        if (data.university_id) {
            additionalData.university_id = data.university_id;
        }

        const response = await this.apiClient.uploadFile<any>("/pascos/student-upload", {
            file: data.file,
            additionalData,
            onUploadProgress: onUploadProgress ? (progressEvent: any) => {
                if (progressEvent.total) {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    onUploadProgress(percentCompleted);
                }
            } : undefined,
        });

        return PascoMap.toDomain(response);
    }

    async update(pasco: Pasco): Promise<Pasco> {
        const dto = PascoMap.toPersistence(pasco);
        const data = await this.apiClient.put<any>(`/pascos/${pasco.pascoId}`, dto);
        return PascoMap.toDomain(data);
    }

    async delete(pascoId: string): Promise<void> {
        await this.apiClient.delete(`/pascos/${pascoId}`);
    }


    async approve(pascoId: string): Promise<Pasco> {
        const data = await this.apiClient.patch<any>("/pascos/approve", {
            pasco_id: pascoId
        });
        return PascoMap.toDomain(data);
    }

    async review(pascoId: string): Promise<Pasco> {
        const data = await this.apiClient.patch<any>(`/pascos/${pascoId}/review`);
        return PascoMap.toDomain(data);
    }

    async scheduleReview(pascoId: string): Promise<Pasco> {
        const data = await this.apiClient.patch<any>(`/pascos/${pascoId}/schedule-review`);
        return PascoMap.toDomain(data);
    }

    async getDownloadUrl(pascoId: string): Promise<PresignedUrl> {
        const data = await this.apiClient.get<any>(`/pascos/${pascoId}/download`);

        return PresignedUrl.create({
            downloadUrl: data.download_url,
            expiresAt: new Date(data.expires_at),
            pascoId: data.pasco_id,
        });
    }

    async downloadStream(pascoId: string): Promise<Blob> {
        return await this.apiClient.downloadBlob(`/pascos/${pascoId}/stream`);
    }

    async getPreviewUrl(pascoId: string): Promise<PreviewUrl> {
        const data = await this.apiClient.get<any>(`/pascos/${pascoId}/preview`);

        return PreviewUrl.create({
            previewUrl: data.preview_url,
            expiresAt: new Date(data.expires_at),
            pascoId: pascoId,
        });
    }

    async search(params: SearchPascoParams): Promise<SearchPascoResult> {
        const queryParams = new URLSearchParams();

        if (params.q) queryParams.append('q', params.q);
        if (params.status) queryParams.append('status', params.status);
        if (params.courseCode) queryParams.append('course_code', params.courseCode);
        if (params.department) queryParams.append('department', params.department);
        if (params.level) queryParams.append('level', params.level.toString());
        if (params.semester) queryParams.append('semester', params.semester);
        if (params.academicYear) queryParams.append('academic_year', params.academicYear);
        if (params.isInterimAssessment !== undefined) {
            queryParams.append('is_interim_assessment', params.isInterimAssessment.toString());
        }
        if (params.isSupplementary !== undefined) {
            queryParams.append('is_supplementary', params.isSupplementary.toString());
        }
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
        if (params.limit) queryParams.append('limit', params.limit.toString());

        const data = await this.apiClient.get<any>(`/search?${queryParams.toString()}`);

        // Debug: Log the API response structure
        console.log('[Search API Response]', data);

        // /search endpoint should return paginated structure
        const results = data?.results || data?.data || [];

        return {
            pascos: Array.isArray(results) ? results.map((p: any) => PascoMap.toDomain(p)) : [],
            total: data?.total || 0,
            page: data?.page || params.page || 1,
            limit: data?.limit || params.limit || 20,
            hasMore: data?.has_more ?? false,
        };
    }

    async listPaginated(params: {
        q?: string;
        status?: string;
        skip?: number;
        limit?: number;
    }): Promise<SearchPascoResult> {
        const queryParams = new URLSearchParams();

        if (params.q) queryParams.append('q', params.q);
        if (params.status) queryParams.append('status', params.status);
        if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
        if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());

        const data = await this.apiClient.get<any>(`/pascos?${queryParams.toString()}`);

        // Debug: Log the API response structure
        if (import.meta.env.DEV) {
            console.log('[List Paginated API Response]', data);
        }

        // Handle paginated response - be more defensive about the response structure
        let results: any[] = [];
        if (Array.isArray(data)) {
            results = data;
        } else if (data && typeof data === 'object') {
            results = data?.results || data?.data || [];
        }

        // Ensure results is an array
        if (!Array.isArray(results)) {
            console.warn('API returned unexpected data structure for pascos:', data);
            results = [];
        }

        // Determine if there are more pages
        let hasMore = false;
        if (data?.has_more !== undefined) {
            hasMore = data.has_more;
        } else if (data?.hasMore !== undefined) {
            hasMore = data.hasMore;
        } else if (data?.page && data?.total_pages) {
            hasMore = data.page < data.total_pages;
        } else {
            // Fallback: if no pagination metadata, check if we got fewer results than requested
            // This indicates we've reached the end of the data
            hasMore = !(results.length < (params.limit || 20));
        }

        return {
            pascos: results.map((p: any) => PascoMap.toDomain(p)),
            total: data?.total || results.length || 0,
            page: data?.page || Math.floor((params.skip || 0) / (params.limit || 20)) + 1,
            limit: data?.limit || params.limit || 20,
            hasMore,
        };
    }

    async getPublicPascos(params: {
        university_id?: string;
        course_code?: string;
        semester?: string;
        academic_year?: string;
        skip?: number;
        limit?: number;
    }): Promise<SearchPascoResult> {
        const queryParams = new URLSearchParams();

        if (params.university_id) {
            queryParams.append('university_id', params.university_id);
        }
        if (params.course_code) {
            queryParams.append('course_code', params.course_code);
        }
        if (params.semester) {
            queryParams.append('semester', params.semester);
        }
        if (params.academic_year) {
            queryParams.append('academic_year', params.academic_year);
        }
        if (params.skip !== undefined) {
            queryParams.append('skip', params.skip.toString());
        }
        if (params.limit !== undefined) {
            queryParams.append('limit', params.limit.toString());
        }

        const queryString = queryParams.toString();
        const url = queryString ? `/public/pascos?${queryString}` : '/public/pascos';

        const data = await this.apiClient.get<any>(url);

        // Handle paginated response
        const results = data?.results || data?.data || data || [];

        return {
            pascos: Array.isArray(results) ? results.map((p: any) => PascoMap.toDomain(p)) : [],
            total: data?.total || results.length || 0,
            page: data?.page || 1,
            limit: data?.limit || params.limit || 20,
            hasMore: data?.has_more ?? !(results.length < (params.limit || 20)),
        };
    }

    async getPublicPascoFilterOptions(): Promise<{
        course_codes: string[];
        semesters: number[];
        academic_years: string[];
    }> {
        const data = await this.apiClient.get<any>('/public/pascos/filter-options');

        return {
            course_codes: data.course_codes || [],
            semesters: data.semesters || [],
            academic_years: data.academic_years || [],
        };
    }

    async getPendingReviewPascos(params?: {
        q?: string;
        university_id?: string;
        semester?: string;
        academic_year?: string;
        skip?: number;
        limit?: number;
    }): Promise<SearchPascoResult> {
        const queryParams = new URLSearchParams();

        if (params?.q) {
            queryParams.append('q', params.q);
        }
        if (params?.university_id) {
            queryParams.append('university_id', params.university_id);
        }
        if (params?.semester) {
            queryParams.append('semester', params.semester);
        }
        if (params?.academic_year) {
            queryParams.append('academic_year', params.academic_year);
        }
        if (params?.skip !== undefined) {
            queryParams.append('skip', params.skip.toString());
        }
        if (params?.limit !== undefined) {
            queryParams.append('limit', params.limit.toString());
        }

        const queryString = queryParams.toString();
        const url = queryString ? `/pascos/pending-review?${queryString}` : '/pascos/pending-review';

        const data = await this.apiClient.get<any>(url);

        // Handle paginated response
        const results = data?.results || data?.data || data || [];

        return {
            pascos: Array.isArray(results) ? results.map((p: any) => PascoMap.toDomain(p)) : [],
            total: data?.total || results.length || 0,
            page: data?.page || 1,
            limit: data?.limit || params?.limit || 20,
            hasMore: data?.has_more ?? !(results.length < (params?.limit || 20)),
        };
    }

    async getPendingApprovalPascos(params?: {
        q?: string;
        university_id?: string;
        semester?: string;
        academic_year?: string;
        skip?: number;
        limit?: number;
    }): Promise<SearchPascoResult> {
        const queryParams = new URLSearchParams();

        if (params?.q) {
            queryParams.append('q', params.q);
        }
        if (params?.university_id) {
            queryParams.append('university_id', params.university_id);
        }
        if (params?.semester) {
            queryParams.append('semester', params.semester);
        }
        if (params?.academic_year) {
            queryParams.append('academic_year', params.academic_year);
        }
        if (params?.skip !== undefined) {
            queryParams.append('skip', params.skip.toString());
        }
        if (params?.limit !== undefined) {
            queryParams.append('limit', params.limit.toString());
        }

        const queryString = queryParams.toString();
        const url = queryString ? `/pascos/pending-approval?${queryString}` : '/pascos/pending-approval';

        const data = await this.apiClient.get<any>(url);

        // Handle paginated response
        const results = data?.results || data?.data || data || [];

        return {
            pascos: Array.isArray(results) ? results.map((p: any) => PascoMap.toDomain(p)) : [],
            total: data?.total || results.length || 0,
            page: data?.page || 1,
            limit: data?.limit || params?.limit || 20,
            hasMore: data?.has_more ?? !(results.length < (params?.limit || 20)),
        };
    }

    async searchPublicPascos(params: {
        q: string;
        university_id?: string;
        semester?: string;
        academic_year?: string;
        skip?: number;
        limit?: number;
    }): Promise<SearchPascoResult> {
        const queryParams = new URLSearchParams();

        queryParams.append('q', params.q);

        if (params.university_id) {
            queryParams.append('university_id', params.university_id);
        }
        if (params.semester) {
            queryParams.append('semester', params.semester);
        }
        if (params.academic_year) {
            queryParams.append('academic_year', params.academic_year);
        }
        if (params.skip !== undefined) {
            queryParams.append('skip', params.skip.toString());
        }
        if (params.limit !== undefined) {
            queryParams.append('limit', params.limit.toString());
        }

        const url = `/pascos/search?${queryParams.toString()}`;

        const data = await this.apiClient.get<any>(url);

        // Handle paginated response
        const results = data?.results || data?.data || data || [];

        return {
            pascos: Array.isArray(results) ? results.map((p: any) => PascoMap.toDomain(p)) : [],
            total: data?.total || results.length || 0,
            page: data?.page || 1,
            limit: data?.limit || params?.limit || 20,
            hasMore: data?.has_more ?? !(results.length < (params?.limit || 20)),
        };
    }

    async extractMetadata(pascoId: string): Promise<ExtractedMetadataDTO> {
        const data = await this.apiClient.post<any>(`/pascos/${pascoId}/extract-metadata`, {});
        
        return {
            pascoId: data.pasco_id,
            academicYear: data.academic_year ?? null,
            courseCode: data.course_code ?? null,
            courseName: data.course_name ?? null,
            isInterimAssessment: data.is_interim_assessment ?? false,
            isSupplementary: data.is_supplementary ?? false,
            level: data.level ?? null,
            semester: data.semester ?? null,
            processingStatus: data.processing_status ?? 'pending',
            message: data.message ?? 'Metadata extracted',
        };
    }

}
